import React, { useState } from 'react';
import { MessageSquare, Star } from 'lucide-react';
import { BaseModal } from './BaseModal';

type FeedbackCategory = 'bug' | 'feature' | 'general';

interface FeedbackButtonProps {
  feedbackEmail?: string; // Kept for backward compatibility but unused
  isAIDrawerOpen?: boolean;
}

const FeedbackButton: React.FC<FeedbackButtonProps> = ({ isAIDrawerOpen = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [category, setCategory] = useState<FeedbackCategory>('general');
  const [message, setMessage] = useState('');
  const [userName, setUserName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{type: 'success' | 'error', message: string} | null>(null);

  const handleSubmit = async () => {
    // Validate required fields
    if (!rating || rating < 1 || rating > 5) {
      setSubmitStatus({ type: 'error', message: 'Please select a rating (1-5 stars)' });
      return;
    }

    if (!message.trim()) {
      setSubmitStatus({ type: 'error', message: 'Please enter your feedback message' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('/api/send-feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rating,
          category,
          message: message.trim(),
          userName: userName.trim() || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to send feedback');
      }

      // Success!
      setSubmitStatus({ type: 'success', message: 'Thank you! Your feedback has been sent successfully.' });

      // Reset form after 2 seconds
      setTimeout(() => {
        setIsModalOpen(false);
        setRating(0);
        setCategory('general');
        setMessage('');
        setUserName('');
        setSubmitStatus(null);
      }, 2000);

    } catch (error) {
      console.error('Failed to send feedback:', error);
      setSubmitStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to send feedback. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating Feedback Button */}
      <button
        onClick={() => setIsModalOpen(true)}
        className={`fixed bottom-6 z-40 bg-blue-500 hover:bg-blue-600 text-white px-4 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 group ${
          isAIDrawerOpen ? 'lg:right-[624px]' : 'right-6'
        }`}
        aria-label="Send Feedback"
      >
        <MessageSquare className="w-5 h-5" />
        <span className="font-medium text-sm">Feedback</span>
      </button>

      {/* Enhanced Feedback Modal */}
      {isModalOpen && (
        <BaseModal
          isOpen={true}
          onClose={() => setIsModalOpen(false)}
          title="Send Feedback"
          maxWidth="lg"
        >
          {/* Star Rating */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              Rating <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="transition-transform hover:scale-110"
                  aria-label={`Rate ${star} stars`}
                >
                  <Star
                    className={`w-8 h-8 ${
                      star <= (hoveredRating || rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-muted-foreground'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Category Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              Category
            </label>
            <div className="flex gap-2">
              {[
                { value: 'bug' as FeedbackCategory, label: 'Bug Report' },
                { value: 'feature' as FeedbackCategory, label: 'Feature Request' },
                { value: 'general' as FeedbackCategory, label: 'General' },
              ].map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setCategory(cat.value)}
                  className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${
                    category === cat.value
                      ? 'border-primary bg-primary/10 text-primary font-medium'
                      : 'border-border text-foreground hover:border-primary/50'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Name Input (Optional) */}
          <div className="mb-4">
            <label htmlFor="userName" className="block text-sm font-medium text-foreground mb-2">
              Your Name (Optional)
            </label>
            <input
              id="userName"
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="John Doe"
              className="w-full px-3 py-2 border border-border bg-background text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Message Textarea */}
          <div className="mb-4">
            <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us what you think..."
              rows={4}
              className="w-full px-3 py-2 border border-border bg-background text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
          </div>

          {/* Status Message */}
          {submitStatus && (
            <div
              className={`mb-4 p-3 rounded-lg ${
                submitStatus.type === 'success'
                  ? 'bg-green-100 text-green-800 border border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800'
                  : 'bg-red-100 text-red-800 border border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800'
              }`}
            >
              {submitStatus.message}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                isSubmitting
                  ? 'bg-muted cursor-not-allowed'
                  : 'bg-primary hover:bg-primary/90'
              } text-primary-foreground font-medium`}
            >
              {isSubmitting ? 'Sending...' : 'Submit Feedback'}
            </button>
            <button
              onClick={() => setIsModalOpen(false)}
              disabled={isSubmitting}
              className="px-4 py-2 border border-border rounded-lg hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-foreground"
            >
              Cancel
            </button>
          </div>
        </BaseModal>
      )}
    </>
  );
};

export default FeedbackButton;
